// ─────────────────────────────────────────────────────────────────────────────
// Tagalys Customisations — wax-london theme
//
// Sections (search the heading text to jump):
//   Global config ............. market data, scroll restoration
//   Tagalys API config ........ server, locale, currency, Shopify platform
//   Layout switcher ........... grid column switcher (2/4 col, mobile/desktop)
//   Pagination helpers ........ TagalysCustomisations.paginationHelpers — page-number window builder
//   Size swatches ............. TagalysCustomisations.sizeSwatches — size option detection and swatch rendering
//   Filter helpers ............ TagalysCustomisations.filterHelpers — category filter ID detection, redundant filter list
//   Model view ................ flat-lay ↔ model image toggle, localStorage
//   Sticky toolbar ............ rAF loop keeping toolbar below the animated header
//   Pagination transition ..... smooth scroll + skeleton overlay + parallel Tagalys fetch
//   Product sliders ........... Flickity carousels, hover/toggle behaviour
//   Collection ................ initCollection — templates + Tagalys widget init
// ─────────────────────────────────────────────────────────────────────────────

const TagalysCustomisations = {};

TagalysCustomisations.DEBUG = false; // flip to true when debugging sliders or model view locally
if (typeof window !== 'undefined' && /[?&]tagalys_debug=1\b/.test(window.location.search)) {
  TagalysCustomisations.DEBUG = true; // temporary: append ?tagalys_debug=1 to any collection URL to enable without redeploying
}

// ── Global config ─────────────────────────────────────────────────────────────
//
// Market/locale data injected by Shopify. Available immediately on script load.

TagalysCustomisations.market = window.__shopMarket;

if (history.scrollRestoration) {
  history.scrollRestoration = 'manual';
}

// ── Tagalys API config ────────────────────────────────────────────────────────
//
// Called from layout/theme.liquid after the Tagalys SDK loads.
// Sets up the API endpoint, locale, currency, and Shopify platform details.

TagalysCustomisations.setTagalysConfiguration = function () {
  Tagalys.setConfiguration({
    api: {
      serverUrl: 'https://api-r4.tagalys.com',
    },
    locale: TagalysCustomisations.market.locale,
    currency: {
      displayFormatter: '{{currencyLabel}}{{value}}',
      code: TagalysCustomisations.market.currencyCode,
      label: TagalysCustomisations.market.currencySymbol,
      fractionalDigits: 2,
      forceFractionalDigits: false,
    },
    analyticsStorageConsentProvided: () => true,
    track: true,
  });
};

TagalysCustomisations.setPlatformConfiguration = function () {
  const config = {
    platform: 'Shopify',
    baseCountryCode: 'GB',
    countryCode: TagalysCustomisations.market.countryCode,
    languageCode: TagalysCustomisations.market.languageCode,
    waitForStorefrontAPI: true,
    useStorefrontAPIForSecondaryMarkets: false,
    storefrontAPI: {
      accessToken: 'e7b81d93a096c4464ef333748797d8ae',
      myShopifyDomain: 'wax-london.myshopify.com',
    },
    metafields: {
      products: [
        { namespace: 'custom', key: 'multibuy' },
        { namespace: 'custom', key: 'back_in_stock_badge_active' },
        { namespace: 'custom', key: 'back_in_stock_badge_us' },
      ],
    },
  }
  Tagalys.setPlatformConfiguration(config);
  return config;
};

// ── Layout switcher ───────────────────────────────────────────────────────────
//
// Controls the product grid column count. State is kept here so Tagalys's
// Preact re-renders can read the current count and keep the active button in
// sync without losing the user's selection.

TagalysCustomisations.layoutSwitcher = {
  state: { mobileCount: 2, desktopCount: 4 },

  /**
   * @param {'mobile'|'desktop'} gridType
   * @param {number} newCount
   * @param {HTMLElement} [clickedBtn]
   */
  changeLayout(gridType, newCount, clickedBtn) {
    const stateKey = gridType === 'mobile' ? 'mobileCount' : 'desktopCount';
    const previousCount = this.state[stateKey];
    if (previousCount === newCount) return;

    this.state[stateKey] = newCount;

    const productList = document.querySelector('[data-tagalys-collection] .ProductList');
    if (productList) {
      productList.setAttribute(`data-${gridType}-count`, newCount);
      productList.querySelectorAll('.Grid__Cell').forEach(item => {
        if (gridType === 'mobile') {
          item.classList.remove(`1/${previousCount}--phone`);
          item.classList.add(`1/${newCount}--phone`);
        } else {
          const prevTablet = previousCount === 2 ? 2 : 3;
          const newTablet = newCount === 2 ? 2 : 3;
          item.classList.remove(`1/${previousCount}--lap-and-up`);
          item.classList.add(`1/${newCount}--lap-and-up`);
          item.classList.remove(`1/${prevTablet}--tablet-and-up`);
          item.classList.add(`1/${newTablet}--tablet-and-up`);
        }
      });
    }

    if (clickedBtn) {
      const switchContainer = clickedBtn.closest('.CollectionToolbar__LayoutSwitch');
      if (switchContainer) {
        switchContainer.querySelectorAll('.CollectionToolbar__LayoutType').forEach(btn => {
          btn.classList.remove('is-active');
        });
        clickedBtn.classList.add('is-active');
      }
    }

    // The grid cells resized, but Flickity caches each cell's pixel width at init and only
    // recalculates on a *window* resize — which a column switch never fires. Without this the
    // carousels keep their old width inside the new cells, so the grid looks unchanged.
    // Rebuild them exactly as on first load, after the browser has committed the reflow.
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => TagalysCustomisations.productSliders.init());
    } else {
      TagalysCustomisations.productSliders.init();
    }
  },

  getGridCellClass() {
    const { mobileCount, desktopCount } = this.state;
    const tabletCount = desktopCount === 2 ? 2 : 3;
    return `Grid__Cell 1/${mobileCount}--phone 1/${tabletCount}--tablet-and-up 1/${desktopCount}--lap-and-up`;
  },
};

// ── Pagination helpers ────────────────────────────────────────────────────────
//
// Builds the array of page items (numbers + ellipsis) shown in the pagination
// bar. Kept separate from the transition logic — this is pure data, no DOM.

TagalysCustomisations.paginationHelpers = {
  buildItems(currentPage, totalPages, windowSize, showAllThreshold) {
    const halfWindow = windowSize != null ? windowSize : 2;
    const allThreshold = showAllThreshold !== undefined ? showAllThreshold : 6;
    const seen = {};
    const pages = [];

    const includePage = (page) => {
      if (page < 1 || page > totalPages || seen[page]) return;
      seen[page] = true;
      pages.push(page);
    };

    if (totalPages <= allThreshold) {
      for (let i = 1; i <= totalPages; i++) includePage(i);
    } else if (currentPage === 1) {
      includePage(1); includePage(2); includePage(3);
      includePage(totalPages);
    } else if (currentPage === totalPages) {
      includePage(1);
      includePage(totalPages - 2); includePage(totalPages - 1); includePage(totalPages);
    } else if (currentPage <= halfWindow + 2) {
      for (let i = 1; i <= currentPage + halfWindow; i++) includePage(i);
      includePage(totalPages);
    } else if (currentPage >= totalPages - halfWindow - 1) {
      includePage(1);
      for (let i = currentPage - halfWindow; i <= totalPages; i++) includePage(i);
    } else {
      includePage(1);
      for (let i = currentPage - halfWindow; i <= currentPage + halfWindow; i++) includePage(i);
      includePage(totalPages);
    }

    pages.sort((a, b) => a - b);

    const items = [];
    for (let i = 0; i < pages.length; i++) {
      if (i > 0 && pages[i] - pages[i - 1] > 1) {
        items.push({ type: 'ellipsis' });
      }
      items.push({ type: 'page', page: pages[i], current: pages[i] === currentPage });
    }

    return items;
  },
};

// ── Size swatches ─────────────────────────────────────────────────────────────
//
// Detects the size option on a product and renders small swatch pills on the
// product card. Supports multiple size label languages.

TagalysCustomisations.sizeSwatches = {
  LABELS: ['size', 'taille', 'größe', 'tamanho', 'tamaño', 'koko', 'サイズ'],

  _getOptionValue(variant, index) {
    // Tagalys variants use option1/option2/option3, not an options array
    if (variant[`option${index + 1}`] != null) return variant[`option${index + 1}`];
    return variant.options && variant.options[index];
  },

  render(html, product) {
    if (!product.available) return null;

    const options = product.options || [];
    const variants = product.variants || [];

    if (!variants.length || !options.length) return null;

    let sizeOptionIndex = -1;
    for (let i = 0; i < options.length; i++) {
      const optName = typeof options[i] === 'string' ? options[i] : (options[i].name || '');
      if (this.LABELS.indexOf(optName.toLowerCase()) !== -1) {
        sizeOptionIndex = i;
        break;
      }
    }

    if (sizeOptionIndex === -1) return null;

    const seenSizes = {};
    const sizeValues = [];
    variants.forEach(variant => {
      const size = this._getOptionValue(variant, sizeOptionIndex);
      if (size && !seenSizes[size]) {
        seenSizes[size] = true;
        sizeValues.push(size);
      }
    });

    if (!sizeValues.length) return null;

    const optionCount = options.length;

    return html`
      <div class="ProductForm__Option ProductForm__Option--card">
        <ul class="SizeSwatchList SizeSwatchList--card HorizontalList HorizontalList--spacingTight">
          ${sizeValues.map(sizeValue => {
            const sizeVariants = variants.filter(v => this._getOptionValue(v, sizeOptionIndex) === sizeValue);

            let swatchClass = '';
            if (optionCount === 1) {
              const v = sizeVariants[0];
              swatchClass = (v && v.available) ? ' plp-SizeSwatch--available' : ' plp-SizeSwatch--unavailable';
            } else {
              const unavailableCount = sizeVariants.filter(v => !v.available).length;
              swatchClass = (sizeVariants.length > 0 && unavailableCount >= sizeVariants.length)
                ? ' plp-SizeSwatch--unavailable'
                : ' plp-SizeSwatch--available';
            }

            return html`
              <li class="HorizontalList__Item">
                <span class=${'plp-SizeSwatch' + swatchClass}>${sizeValue}</span>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  },
};

// ── Filter helpers ────────────────────────────────────────────────────────────
//
// Tagalys and Shopify's native Search & Discovery both surface the category
// size filter, but with different ID prefixes. We match on the suffix so the
// same UI code works for both. We also drop Shopify's generic variant size
// filter since we already render it via the custom size-box UI.

TagalysCustomisations.filterHelpers = {
  REDUNDANT_IDS: ['filter.v.option.size', '_variant_size'],

  isCategoryId(filterId) {
    return typeof filterId === 'string' && filterId.slice(-'filter_category'.length) === 'filter_category';
  },

  isProductTypeId(filterId) {
    return typeof filterId === 'string' && filterId.slice(-'product_type'.length) === 'product_type';
  },
};

// ── Model view ────────────────────────────────────────────────────────────────
//
// Toggles product cards between flat-lay (slide 0) and model (slide 1) images.
// State persists to localStorage so the user's choice survives page navigation.
// Dispatches custom events so the slider system can react independently — this
// keeps model view logic decoupled from the Flickity implementation.

TagalysCustomisations.modelView = {
  isOn() {
    const btn = document.querySelector('.CollectionToolbar__Item--model-view');
    return btn && btn.getAttribute('aria-expanded') === 'true';
  },

  /**
   * Called via inline onclick on the model-view toolbar button.
   * @param {HTMLElement} btn
   */
  toggle(btn) {
    const isOn = btn.getAttribute('aria-expanded') === 'true';
    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] modelView.toggle called. Currently on:', isOn, '| readyState:', document.readyState, '| Flickity:', !!window.Flickity, '| sliders:', TagalysCustomisations.productSliders._list.length);
    btn.setAttribute('aria-expanded', isOn ? 'false' : 'true');
    localStorage.setItem('wax-collection-model-view', isOn ? 'off' : 'on');
    document.documentElement.classList.toggle('collection-model-view-on', !isOn);
    document.documentElement.classList.toggle('collection-model-view-off', isOn);
    const collectionList = document.getElementById('collection-list');
    if (collectionList) collectionList.classList.toggle('ProductList--switch', !isOn);
    document.dispatchEvent(new CustomEvent(isOn ? 'plpModelViewToggleOff' : 'plpModelViewToggleOn'));
  },

  _handleToggleOn() {
    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] modelView._handleToggleOn fired. Sliders:', TagalysCustomisations.productSliders._list.length);
    document.querySelectorAll('[data-tagalys-collection] .ProductItem__Wrapper').forEach(wrapper => {
      wrapper.classList.remove('js-slider-interacted');
    });
    TagalysCustomisations.productSliders._list.forEach(slider => {
      if (slider.cells && slider.cells.length >= 2) slider.select(1, false, false);
    });
  },

  _handleToggleOff() {
    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] modelView._handleToggleOff fired. Sliders:', TagalysCustomisations.productSliders._list.length);
    document.querySelectorAll('[data-tagalys-collection] .ProductItem__Wrapper').forEach(wrapper => {
      wrapper.classList.remove('js-slider-interacted');
    });
    TagalysCustomisations.productSliders._list.forEach(slider => slider.select(0, false, false));
  },
};

// ── Sticky toolbar ────────────────────────────────────────────────────────────
//
// Keeps .plp-info-and-actions pinned directly below the main site header.
// The header shows/hides via a CSS transition (~300ms on scroll), so a one-shot
// scroll/resize listener would miss the trailing animation. A requestAnimationFrame
// loop stays in sync with wherever the header actually is at any given frame.
// Two possible header IDs exist depending on which market is active.

TagalysCustomisations.stickyToolbar = {
  _el: null,
  _raf: null,

  _update() {
    const headerEl = document.getElementById('shopify-section-header') || document.getElementById('shopify-section-header-us');
    if (!headerEl) return;

    if (!this._el || !this._el.isConnected) {
      this._el = document.querySelector('[data-tagalys-collection] .plp-info-and-actions');
    }
    if (!this._el) return;

    this._el.style.top = `${Math.max(headerEl.getBoundingClientRect().bottom, 0)}px`;
  },

  teardown() {
    if (this._raf) {
      window.cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  },

  init() {
    this.teardown();
    this._el = null;

    const tick = () => {
      this._update();
      this._raf = window.requestAnimationFrame(tick);
    };
    tick();
  },
};

// ── Pagination transition ─────────────────────────────────────────────────────
//
// When a page link is clicked: smooth-scroll to the top while Tagalys fetches
// the next page in parallel (capture-phase listener, no stopImmediatePropagation).
// A skeleton overlay injected outside the Preact root covers old/new products
// during the scroll so nothing flickers mid-animation.
//
// Two signals must fire before the overlay is removed:
//   1. scrollDone — scroll animation completed (scrollend event or debounce fallback)
//   2. renderDone — Tagalys finished rendering the new page (onRenderDone callback)
//
// Container min-height is locked during the transition so iOS Safari doesn't
// clamp window.scrollY mid-animation when the last page has fewer products.

TagalysCustomisations.paginationTransition = {
  _state: null,
  MIN_SKELETON_MS: 400, // minimum time skeleton stays visible after scroll lands — tweak here

  _buildSkeletonOverlay(container) {
    const collectionList = document.getElementById('collection-list');
    const count = collectionList
      ? (collectionList.querySelectorAll('.ProductItem__Wrapper').length || 8)
      : 8;

    // Align the skeleton grid with where products actually start (below the toolbar).
    let topOffset = 60;
    if (collectionList && container) {
      const gap = collectionList.getBoundingClientRect().top - container.getBoundingClientRect().top;
      if (gap > 0) topOffset = gap;
    }

    const cards = Array.from({ length: count }, () => `
      <div class="c-plp-skeleton__card">
        <div class="c-plp-skeleton__image"></div>
        <div class="c-plp-skeleton__body">
          <div class="c-plp-skeleton__line c-plp-skeleton__line--title"></div>
          <div class="c-plp-skeleton__line c-plp-skeleton__line--price"></div>
        </div>
      </div>
    `).join('');

    const el = document.createElement('div');
    el.className = 'c-plp-pagination-skeleton-overlay';
    el.innerHTML = `<div class="c-plp-skeleton" style="padding-top:${topOffset}px">${cards}</div>`;
    return el;
  },

  // Called by paginationLinks.options.scrollTop after Tagalys finishes rendering.
  onRenderDone() {
    const state = TagalysCustomisations.paginationTransition._state;
    if (!state) return;
    state.renderDone = true;
    state.tryReveal();
  },

  init() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.Pagination__NavItem.btn:not(.c-pagination-doted-span)');
      if (!btn || !btn.closest('[data-tagalys-collection]')) return;
      if (window.scrollY === 0) return;

      const container = document.querySelector('[data-tagalys-collection]');
      if (container) {
        container.style.minHeight = `${container.offsetHeight}px`;
      }

      const skeletonOverlay = this._buildSkeletonOverlay(container);
      if (container) container.appendChild(skeletonOverlay);

      const state = {
        scrollDone: false,
        renderDone: false,
        tryReveal() {
          if (TagalysCustomisations.paginationTransition._state !== state) return;
          if (!state.scrollDone || !state.renderDone) return;
          TagalysCustomisations.paginationTransition._state = null;
          if (container) container.style.minHeight = '';

          // Cross-fade: remove body class (products fade in) while skeleton fades out simultaneously.
          document.body.classList.remove('is-pagination-transitioning');
          skeletonOverlay.classList.add('is-fading-out');
          setTimeout(() => {
            if (skeletonOverlay.parentNode) skeletonOverlay.parentNode.removeChild(skeletonOverlay);
          }, 400); // matches .c-plp-pagination-skeleton-overlay transition duration
        },
      };

      TagalysCustomisations.paginationTransition._state = state;
      document.body.classList.add('is-pagination-transitioning');
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // No stopImmediatePropagation — Tagalys receives the click and fetches in parallel.

      const onScrollDone = () => {
        // Hold the skeleton visible for MIN_SKELETON_MS after scroll lands,
        // ensuring users always see the loading state before the cross-fade begins.
        setTimeout(() => {
          state.scrollDone = true;
          state.tryReveal();
        }, TagalysCustomisations.paginationTransition.MIN_SKELETON_MS);
      };

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', onScrollDone, { once: true });
      } else {
        // scrollend not supported (iOS Safari < 16.2) — debounce the scroll event instead.
        let timer;
        const onScroll = () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            window.removeEventListener('scroll', onScroll);
            onScrollDone();
          }, 100);
        };
        window.addEventListener('scroll', onScroll);
      }
    }, true); // capture phase — fires before Tagalys's own onclick handlers
  },
};

// ── Product sliders ───────────────────────────────────────────────────────────
//
// Each product card has a Flickity carousel (flat-lay image → model image).
// Hovering a card shows the model image; leaving returns to flat-lay, unless
// the user manually swiped the carousel (tracked via js-slider-interacted).
//
// Flickity is bundled in the deferred theme.js, so it may not be available
// when Tagalys first calls init() on slow connections. We schedule a retry on
// DOMContentLoaded if Flickity isn't present yet.

TagalysCustomisations.productSliders = {
  _list: [],
  _cleanups: [],
  _retryPending: false,

  _destroy() {
    this._cleanups.forEach(fn => fn());
    this._list.forEach(slider => { if (slider?.destroy) slider.destroy(); });
    this._list = [];
    this._cleanups = [];
    document.removeEventListener('plpModelViewToggleOn', TagalysCustomisations.modelView._handleToggleOn);
    document.removeEventListener('plpModelViewToggleOff', TagalysCustomisations.modelView._handleToggleOff);
  },

  init() {
    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] productSliders.init called. readyState:', document.readyState, '| Flickity:', !!window.Flickity, '| retryPending:', this._retryPending);

    if (!window.Flickity) {
      // readyState is 'interactive' once HTML parsing is done — deferred scripts run
      // between 'interactive' and DOMContentLoaded, so we must check both, not just 'loading'.
      if (document.readyState !== 'complete' && !this._retryPending) {
        this._retryPending = true;
        if (TagalysCustomisations.DEBUG) console.log('[Tagalys] Flickity not ready (readyState:', document.readyState, ') — scheduling DOMContentLoaded retry');
        document.addEventListener('DOMContentLoaded', () => {
          TagalysCustomisations.productSliders._retryPending = false;
          if (TagalysCustomisations.DEBUG) console.log('[Tagalys] DOMContentLoaded retry fired. Flickity:', !!window.Flickity);
          if (window.Flickity) TagalysCustomisations.productSliders.init();
        }, { once: true });
      } else {
        if (TagalysCustomisations.DEBUG) console.warn('[Tagalys] Flickity missing and readyState is complete — cannot retry.');
      }
      return;
    }

    this._destroy();

    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const allCarousels = document.querySelectorAll('.main-carousel');
    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] .main-carousel elements found:', allCarousels.length);

    allCarousels.forEach(carouselEl => {
      if (carouselEl.classList.contains('flickity-enabled')) {
        carouselEl.setAttribute('data-slider-status', 'skipped-already-initialized');
        if (TagalysCustomisations.DEBUG) console.log('[Tagalys] Skipping carousel — already flickity-enabled');
        return;
      }

      const slideCount = carouselEl.querySelectorAll('.carousel-cell img').length;
      carouselEl.setAttribute('data-slide-count', slideCount);
      if (slideCount < 2) {
        carouselEl.setAttribute('data-slider-status', 'skipped-single-slide');
        if (TagalysCustomisations.DEBUG) console.log('[Tagalys] Skipping carousel — slideCount:', slideCount);
        return;
      }

      const slider = new window.Flickity(carouselEl, {
        cellSelector: '.carousel-cell',
        contain: true,
        wrapAround: true,
        pageDots: true,
        prevNextButtons: true,
        imagesLoaded: true,
        cellAlign: 'left',
        // Pixel-based (not percentage-based) cell positioning avoids a rounding
        // mismatch with the slider's own pixel transform that otherwise leaves
        // a sliver of the adjacent cell visible: https://github.com/metafizzy/flickity/issues/276
        percentPosition: false,
        arrowShape: { x0: 20, x1: 60, y1: 40, x2: 60, y2: 35, x3: 25 },
        selectedAttraction: isMobile ? 0.01 : 1,
        friction: isMobile ? 0.15 : 1,
      });

      this._list.push(slider);

      slider.on('change', () => {
        if (window.lazySizes?.loader && typeof window.lazySizes.loader.checkElems === 'function') {
          window.lazySizes.loader.checkElems();
        }
      });

      const productItemWrapper = carouselEl.closest('.ProductItem__Wrapper');
      if (!productItemWrapper) {
        carouselEl.setAttribute('data-slider-status', 'skipped-no-wrapper');
        return;
      }

      const hasMultipleSlides = slider.cells && slider.cells.length >= 2;

      const handlePrevClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        productItemWrapper.classList.add('js-slider-interacted');
      };

      const handleNextClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        productItemWrapper.classList.add('js-slider-interacted');
      };

      const handleMouseEnter = () => {
        if (!hasMultipleSlides || productItemWrapper.classList.contains('js-slider-interacted')) return;
        slider.select(TagalysCustomisations.modelView.isOn() ? 0 : 1, false, true);
      };

      const handleMouseLeave = () => {
        if (!hasMultipleSlides || productItemWrapper.classList.contains('js-slider-interacted')) return;
        slider.select(TagalysCustomisations.modelView.isOn() ? 1 : 0, false, true);
      };

      const prevBtn = carouselEl.querySelector('.flickity-prev-next-button.previous');
      const nextBtn = carouselEl.querySelector('.flickity-prev-next-button.next');

      carouselEl.setAttribute('data-slider-status', 'initialized');
      carouselEl.setAttribute('data-slider-prev-btn', prevBtn ? 'found' : 'missing');
      carouselEl.setAttribute('data-slider-next-btn', nextBtn ? 'found' : 'missing');

      if (prevBtn) prevBtn.addEventListener('click', handlePrevClick);
      if (nextBtn) nextBtn.addEventListener('click', handleNextClick);
      productItemWrapper.addEventListener('mouseenter', handleMouseEnter);
      productItemWrapper.addEventListener('mouseleave', handleMouseLeave);

      this._cleanups.push(() => {
        if (prevBtn) prevBtn.removeEventListener('click', handlePrevClick);
        if (nextBtn) nextBtn.removeEventListener('click', handleNextClick);
        productItemWrapper.removeEventListener('mouseenter', handleMouseEnter);
        productItemWrapper.removeEventListener('mouseleave', handleMouseLeave);
      });
    });

    // Preact creates a fresh #collection-list on every render, so re-apply the switch class.
    const collectionList = document.getElementById('collection-list');
    if (collectionList) {
      collectionList.classList.toggle('ProductList--switch', TagalysCustomisations.modelView.isOn());
    }

    if (TagalysCustomisations.modelView.isOn()) {
      this._list.forEach(slider => {
        if (slider.cells && slider.cells.length >= 2) slider.select(1, false, true);
      });
    }

    if (TagalysCustomisations.DEBUG) console.log('[Tagalys] Attaching model view toggle listeners. Sliders initialized:', this._list.length);
    document.addEventListener('plpModelViewToggleOn', TagalysCustomisations.modelView._handleToggleOn);
    document.addEventListener('plpModelViewToggleOff', TagalysCustomisations.modelView._handleToggleOff);

    if (window.lazySizes) {
      if (window.lazySizes.loader && typeof window.lazySizes.loader.checkElems === 'function') {
        window.lazySizes.loader.checkElems();
      }
      if (window.lazySizes.autoSizer && typeof window.lazySizes.autoSizer.checkElems === 'function') {
        window.lazySizes.autoSizer.checkElems();
      }
    }
  },
};

// ── Collection ────────────────────────────────────────────────────────────────
//
// Entry point called from sections/main-collection.liquid. Its only job is to
// start the feature systems and boot the Tagalys widget with the right templates.
// All the heavy logic lives in the feature objects above.
//
// commonTemplates must stay here because it closes over the contentSlotsConfig
// and description parameters passed in from the Liquid template.

/**
 * @param {string} collectionId — Shopify collection GID from main-collection.liquid
 * @param {{ contentSlotsConfig?: object, description?: string, labels?: object }} options
 */
TagalysCustomisations.initCollection = function (collectionId, { contentSlotsConfig, description, hideModelViewOnDesktop, labels }) {
  contentSlotsConfig = contentSlotsConfig || { showContentSlot: false, slotCount: 0, slots: [] };

  // Translated UI copy, passed in from main-collection.liquid so every string goes through
  // Shopify's `| t` filter. It has to arrive this way: this script is loaded in <head>
  // (theme.liquid) well before window.languages is defined, so there is nothing to read
  // from at load time. Named `copy` because the render functions below already bind
  // `labels` to Tagalys's own args.labels.
  const copy = labels || {};
  const badgeCopy = copy.badges || {};
  const sizeCategoryCopy = copy.sizeCategories || {};

  TagalysCustomisations.stickyToolbar.init();
  TagalysCustomisations.paginationTransition.init();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const _iosPreventScroll = (e) => {
    if (!e.target.closest('.filters-content')) e.preventDefault();
  };

  const countAppliedFilters = (appliedFilters) =>
    appliedFilters.reduce((sum, f) => sum + (f.type === 'range' ? 1 : (f.items ? f.items.length : 0)), 0);

  // Size filter items arrive from Tagalys as "<category>_<size>" (e.g. "bottoms_30"), and that
  // category half is raw data — it is not translated. Map it through the same copy the size-box
  // headings use so applied-filter chips read "Bas : 30" on /fr rather than "Bottoms: 30".
  const localizedSizeCategory = (category) =>
    sizeCategoryCopy[(category || '').toLowerCase()] || category;

  const commonTemplates = {
    widget: {
      labels: {
        productListingPage: {
          productsCount: copy.productsCountForCollection,
          noResultsHeading: copy.noResultsHeading,
        },
        filters: {
          heading: copy.filterAndSort,
          clearAllFilters: copy.clearAllFilters,
          clearIndividualFilter: copy.clearIndividualFilter,
          drawerFilterToggle: copy.filterAndSort,
          rangeFilterValueSeparator: copy.rangeSeparator,
        },
        sortOptions: {
          sortBy: copy.sortBy,
        },
        pagination: {
          firstPage: copy.firstPage,
          previousPage: copy.previousPage,
          nextPage: copy.nextPage,
          loadNext: copy.loadMore,
          loading: copy.loading,
        },
      },
      options: {
        canAppendQueryStringToUrl: true,
        perPage: 48,
        includeTagalysCSS: false,
      },
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;

        if (helpers.isInitialLoading()) {
          return html` <${args.templates.loader} /> `;
        }

        const isDrawerOpen = helpers.isFilterDrawerOpen();
        const wasDrawerOpen = document.body.classList.contains('is-filter-drawer-open');
        if (isIOS) {
          if (isDrawerOpen && !wasDrawerOpen) {
            document.addEventListener('touchmove', _iosPreventScroll, { passive: false });
          } else if (!isDrawerOpen && wasDrawerOpen) {
            document.removeEventListener('touchmove', _iosPreventScroll);
          }
        }
        document.body.classList.toggle('is-filter-drawer-open', isDrawerOpen);

        const classes = ['plp'];
        if (helpers.isFilterDrawerOpen()) classes.push('filter-drawer-opened');
        if (helpers.isLoading()) classes.push('loading');
        if (helpers.canShowFiltersInDrawer()) classes.push('show-filters-in-drawer');

        if (helpers.hasNoProducts() && !helpers.hasAnyFiltersApplied()) {
          return html`
            <div class="plp">
              <${args.templates.noResults} />
            </div>
          `;
        }

        return html`
          <div class=${classes.join(' ')}>
            ${helpers.isFilterDrawerOpen() ? html` <div class="filter-overlay PageOverlay is-visible" onclick=${helpers.closeFilterDrawer}/>` : null}
            <div class="filters-and-products-list">
              <${args.templates.filters} />
              <div class="products-list">
                <${args.templates.productListHeader} />
                <${args.templates.products} />
                <${args.templates.productListFooter} />
              </div>
            </div>
            <${args.templates.footer} />
          </div>
        `;
      },
    },

    loader: {
      render(html, args) {
        const hasContentSlots = contentSlotsConfig.showContentSlot && contentSlotsConfig.slots.length > 0;
        const layoutState = TagalysCustomisations.layoutSwitcher.state;
        const filterLabel = (args.labels && args.labels.filters && args.labels.filters.drawerFilterToggle) || copy.filterAndSort;
        const cards = Array.from({ length: 8 });

        return html`
          <div class="plp">
            <div class="filters-and-products-list">
              <div class="products-list">
                <div class="products-list-header">
                  <div class="plp-info-and-actions">
                    <div class="CollectionToolbar__Item CollectionToolbar__Item--layout c-grid-layout-switch-container">
                      ${!hasContentSlots ? html`
                        <div class="CollectionToolbar__LayoutSwitch hidden-tablet-and-up">
                          <button
                            aria-label=${copy.showOnePerRow}
                            class=${`CollectionToolbar__LayoutType${layoutState.mobileCount === 1 ? ' is-active' : ''}`}
                            onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('mobile', 1, e.currentTarget)}
                          >
                            <svg role="presentation" viewBox="0 0 36 36"><rect fill="currentColor" width="36" height="36"></rect></svg>
                          </button>
                          <button
                            aria-label=${copy.showTwoPerRow}
                            class=${`CollectionToolbar__LayoutType${layoutState.mobileCount === 2 ? ' is-active' : ''}`}
                            onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('mobile', 2, e.currentTarget)}
                          >
                            <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M21 36V21h15v15H21zm0-36h15v15H21V0zM0 21h15v15H0V21zM0 0h15v15H0V0z"></path></svg>
                          </button>
                        </div>
                        <div class="CollectionToolbar__LayoutSwitch hidden-phone">
                          <button
                            aria-label=${copy.showTwoPerRow}
                            class=${`CollectionToolbar__LayoutType${layoutState.desktopCount === 2 ? ' is-active' : ''}`}
                            onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('desktop', 2, e.currentTarget)}
                          >
                            <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M21 36V21h15v15H21zm0-36h15v15H21V0zM0 21h15v15H0V21zM0 0h15v15H0V0z"></path></svg>
                          </button>
                          <button
                            aria-label=${copy.showFourPerRow}
                            class=${`CollectionToolbar__LayoutType${layoutState.desktopCount !== 2 ? ' is-active' : ''}`}
                            onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('desktop', 4, e.currentTarget)}
                          >
                            <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M28 36v-8h8v8h-8zm0-22h8v8h-8v-8zm0-14h8v8h-8V0zM14 28h8v8h-8v-8zm0-14h8v8h-8v-8zm0-14h8v8h-8V0zM0 28h8v8H0v-8zm0-14h8v8H0v-8zM0 0h8v8H0V0z"></path></svg>
                          </button>
                        </div>
                      ` : null}
                      <button
                        class="CollectionToolbar__Item CollectionToolbar__Item--model-view Collapsible__Button c-model-view-toggle-button-desktop"
                        data-action="model-view"
                        aria-expanded=${localStorage.getItem('wax-collection-model-view') === 'on' ? 'true' : 'false'}
                        aria-controls="collection-list"
                        aria-label=${copy.modelView}
                        onclick=${(e) => { TagalysCustomisations.modelView.toggle(e.currentTarget); e.stopPropagation(); }}
                      >
                        ${copy.modelView}
                        <span class="Collapsible__Toggle"></span>
                      </button>
                    </div>
                    <div class="info-and-filter-toggle CollectionToolbar__Group">
                      <button class="filter-drawer-toggle CollectionToolbar__Item" style="pointer-events:none">
                        ${filterLabel}
                        <span class="filter-drawer-toggle-icon c-filter-and-sort-button__filter-count Collapsible__Plus"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="c-plp-skeleton" aria-hidden="true">
                  ${cards.map(() => html`
                    <div class="c-plp-skeleton__card">
                      <div class="c-plp-skeleton__image"></div>
                      <div class="c-plp-skeleton__body">
                        <div class="c-plp-skeleton__line c-plp-skeleton__line--title"></div>
                        <div class="c-plp-skeleton__line c-plp-skeleton__line--price"></div>
                      </div>
                    </div>
                  `)}
                </div>
              </div>
            </div>
          </div>
        `;
      },
    },

    header: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const apiResponse = helpers.getAPIResponse();

        return html`
          <header class="PageHeader">
            <div class="Container">
              <div class="SectionHeader SectionHeader--center">
                <h1 class="SectionHeader__Heading Heading u-h3">${apiResponse.title}</h1>
                ${description ? html`
                  <div class="SectionHeader__Description Rte" dangerouslySetInnerHTML=${{ __html: description }}></div>
                ` : null}
              </div>
            </div>
          </header>
        `;
      },
    },

    productListHeader: {
      render(html, args) {
        return html`
          <div class="products-list-header">
            <${args.templates.infoAndActions} />
          </div>
        `;
      },
    },

    infoAndActions: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const labels = args.labels;
        const appliedFilters = helpers.getAppliedFilters(true)
          .filter(f => !TagalysCustomisations.filterHelpers.REDUNDANT_IDS.includes(f.id));
        const appliedFiltersCount = countAppliedFilters(appliedFilters);
        const layoutState = TagalysCustomisations.layoutSwitcher.state;
        const hasContentSlots = contentSlotsConfig.showContentSlot && contentSlotsConfig.slots.length > 0;

        return html`
          <div class="plp-info-and-actions">
            <div class="CollectionToolbar__Item CollectionToolbar__Item--layout c-grid-layout-switch-container">
              ${!hasContentSlots ? html`
                <div class="CollectionToolbar__LayoutSwitch hidden-tablet-and-up">
                  <button
                    aria-label=${copy.showOnePerRow}
                    class=${`CollectionToolbar__LayoutType${layoutState.mobileCount === 1 ? ' is-active' : ''}`}
                    onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('mobile', 1, e.currentTarget)}
                  >
                    <svg role="presentation" viewBox="0 0 36 36"><rect fill="currentColor" width="36" height="36"></rect></svg>
                  </button>
                  <button
                    aria-label=${copy.showTwoPerRow}
                    class=${`CollectionToolbar__LayoutType${layoutState.mobileCount === 2 ? ' is-active' : ''}`}
                    onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('mobile', 2, e.currentTarget)}
                  >
                    <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M21 36V21h15v15H21zm0-36h15v15H21V0zM0 21h15v15H0V21zM0 0h15v15H0V0z"></path></svg>
                  </button>
                </div>
                <div class="CollectionToolbar__LayoutSwitch hidden-phone">
                  <button
                    aria-label=${copy.showTwoPerRow}
                    class=${`CollectionToolbar__LayoutType${layoutState.desktopCount === 2 ? ' is-active' : ''}`}
                    onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('desktop', 2, e.currentTarget)}
                  >
                    <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M21 36V21h15v15H21zm0-36h15v15H21V0zM0 21h15v15H0V21zM0 0h15v15H0V0z"></path></svg>
                  </button>
                  <button
                    aria-label=${copy.showFourPerRow}
                    class=${`CollectionToolbar__LayoutType${layoutState.desktopCount !== 2 ? ' is-active' : ''}`}
                    onclick=${(e) => TagalysCustomisations.layoutSwitcher.changeLayout('desktop', 4, e.currentTarget)}
                  >
                    <svg role="presentation" viewBox="0 0 36 36"><path fill="currentColor" d="M28 36v-8h8v8h-8zm0-22h8v8h-8v-8zm0-14h8v8h-8V0zM14 28h8v8h-8v-8zm0-14h8v8h-8v-8zm0-14h8v8h-8V0zM0 28h8v8H0v-8zm0-14h8v8H0v-8zM0 0h8v8H0V0z"></path></svg>
                  </button>
                </div>
              ` : null}
              <button
                class=${`CollectionToolbar__Item CollectionToolbar__Item--model-view Collapsible__Button c-model-view-toggle-button-desktop${hideModelViewOnDesktop ? ' hidden-lap-and-up' : ''}`}
                data-action="model-view"
                aria-expanded=${localStorage.getItem('wax-collection-model-view') === 'on' ? 'true' : 'false'}
                aria-controls="collection-list"
                aria-label=${copy.modelView}
                onclick=${(e) => { TagalysCustomisations.modelView.toggle(e.currentTarget); e.stopPropagation(); }}
              >
                ${copy.modelView}
                <span class="Collapsible__Toggle"></span>
              </button>
            </div>
            <div class="info-and-filter-toggle CollectionToolbar__Group">
              <button
                onclick=${helpers.openFilterDrawer}
                class="filter-drawer-toggle CollectionToolbar__Item"
              >
                ${labels.filters.drawerFilterToggle}
                ${appliedFiltersCount > 0 ? html`
                  <span class="filter-drawer-toggle-count">(${appliedFiltersCount})</span>
                ` : html`
                  <span class="filter-drawer-toggle-icon c-filter-and-sort-button__filter-count Collapsible__Plus"></span>
                `}
              </button>
            </div>
          </div>
        `;
      },
    },

    filters: {
      options: {
        collapsible: true,
        includeSortOptionsInFilters: 'top',
        autoCollapse: false,
        showAppliedFilters: true,
        showClearAllFilters: true,
        showClearActionForIndividualFilters: true,
        showMatchingProductsCount: true,
        defaultExpandedFilters: [],
        showFiltersInDrawer: () => true,
      },
      render(html, args) {
        const props = args.props;
        const labels = args.labels;
        const helpers = props.helpers;

        const filters = helpers.getFilters().filter(filter => {
          return TagalysCustomisations.filterHelpers.REDUNDANT_IDS.indexOf(filter.id) === -1;
        });

        const filterCategoryFilter = filters.find(filter => TagalysCustomisations.filterHelpers.isCategoryId(filter.id));
        const filterCategoryItems = filterCategoryFilter?.items;
        const filterCategoryItemLabels = filterCategoryItems?.map(({ label }) => label);

        const tops = filterCategoryItemLabels?.filter(label => label.includes('tops'));
        const topsSizes = tops?.map(item => item.split('_')[1]);

        const bottoms = filterCategoryItemLabels?.filter(label => label.includes('bottoms'));
        const bottomsSizes = bottoms?.map(item => item.split('_')[1]);

        const accessories = filterCategoryItemLabels?.filter(label => label.includes('accessories'));
        const accessoriesSizes = accessories?.map(item => item.split('_')[1]);

        const footwear = filterCategoryItemLabels?.filter(label => label.includes('footwear'));
        const footwearSizes = footwear?.map(item => item.split('_')[1]);

        const appliedFilters = helpers.getAppliedFilters(true);
        const showClearAllFiltersAction = props.showClearAllFilters && appliedFilters.length;
        const classes = ['filters'];
        if (props.collapsible) classes.push('collapsible');

        const clearAllAndResetSort = () => {
          helpers.setRequestState((requestState) => ({
           ...requestState,
           filters: {},
           sort: "" // defautls to sort by trending
          }));
          scrollToTop();
        };

        return html`
          <div class=${classes.join(' ')}>
            <div class="filters-content">
              <${args.templates.filtersHeader} />
              ${props.showAppliedFilters ? html` <${args.templates.appliedFilters} /> ` : null}
              ${showClearAllFiltersAction ? html`
                <div class="clear-all-filters" onclick=${clearAllAndResetSort}>
                  ${labels.filters.clearAllFilters}
                </div>
              ` : null}
              ${props.includeSortOptionsInFilters === 'top' ? html` <${args.templates.sortOptionInFilters} /> ` : null}
              ${filters.map(filter => html`<${args.templates.filter} filter=${filter} />`)}
              <!--<${args.templates.sizeFilters} topsSizes=${topsSizes} bottomsSizes=${bottomsSizes} accessoriesSizes=${accessoriesSizes} footwearSizes=${footwearSizes} />-->
              ${props.includeSortOptionsInFilters === true || props.includeSortOptionsInFilters === 'bottom'
                ? html` <${args.templates.sortOptionInFilters} /> `
                : null}
            </div>
            <div class="filters-footer-buttons">
              <button class="filters-footer-button" onclick=${clearAllAndResetSort}>${copy.clearSelection}</button>
              <button class="filters-footer-button filters-footer-button--view-results" onclick=${helpers.closeFilterDrawer}>${copy.viewResults} (${helpers.getTotalProductsCount()})</button>
            </div>
          </div>
        `;
      },
    },

    filter: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const classes = ['filter'];

        if (props.collapsible) {
          const isOpen = helpers.isCollapsibleFilterExpanded(props.filter.id);
          classes.push(isOpen ? 'expanded' : 'collapsed');
        }

        return html`
          <div class=${classes.join(' ')} data-filter-id=${props.filter.id}>
            <${args.templates.filterHeader}
              filterHeading=${props.filter.label}
              filterId=${props.filter.id}
            />
            <${args.templates.filterItems} filter=${props.filter} />
          </div>
        `;
      },
    },

    filterHeader: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const labels = args.labels;
        const filterOptions = helpers.getTemplateOptions('filters');
        const showClearAction = filterOptions.showClearActionForIndividualFilters && helpers.isFilterApplied(props.filterId);

        // Two filters are shown under a different name than the one Shopify gives them, matching
        // the native (non-Tagalys) drawer:
        //
        //   Shopify label    shown to customers as
        //   ---------------  ---------------------
        //   Product Type     Product Category
        //   Filter Category  Size
        //
        // Match on the filter id, never on the label: Tagalys returns the label already
        // translated by Translate & Adapt, so testing it against English text fails on /fr-fr
        // and /de-de and the raw translated label leaks through to the customer.
        const filterHelpers = TagalysCustomisations.filterHelpers;
        let filterHeading = props.filterHeading;

        if (filterHelpers.isCategoryId(props.filterId)) {
          filterHeading = copy.sizeHeading || filterHeading;
        } else if (filterHelpers.isProductTypeId(props.filterId)) {
          filterHeading = copy.productCategoryHeading || filterHeading;
        }

        const toggleCollapse = () => helpers.toggleCollapsibleFilter(props.filterId);
        const clearFilter = (e) => {
          e.stopPropagation();
          helpers.clearFilter(props.filterId);
          scrollToTop();
        };

        return html`
          <div
            onClick=${filterOptions.collapsible ? toggleCollapse : undefined}
            class="filter-header"
          >
            <h3 class="filter-heading">${filterHeading}</h3>
            <div class="filter-actions">
              ${showClearAction ? html`
                <div onClick=${clearFilter} class="filter-action-clear">
                  ${labels.filters.clearIndividualFilter}
                </div>
              ` : null}
              <span class="filter-action-toggle-collapse">
                ${filterOptions.collapsible ? html` <${args.templates.downArrow} /> ` : null}
              </span>
            </div>
          </div>
        `;
      },
    },

    filterItems: {
      render(html, args) {
        const props = args.props;

        if (TagalysCustomisations.filterHelpers.isCategoryId(props.filter.id)) {
          return html`
            <div class="filter-body filter-body-sizeboxes">
              <${args.templates.filterSizeBoxes}
                filterId=${props.filter.id}
                filterItems=${props.filter.items}
              />
            </div>
          `;
        }

        if (props.filter.type === 'checkbox') {
          return html`
            <div class="filter-body filter-body-checkboxes">
              <${args.templates.filterCheckBoxes}
                filterId=${props.filter.id}
                filterItems=${props.filter.items}
              />
            </div>
          `;
        }

        return html`
          <div class="filter-body filter-body-range">
            <${args.templates.rangeSliderFilterItem} filter=${props.filter} />
          </div>
        `;
      },
    },

    filterSizeBoxes: {
      render(html, args) {
        const props = args.props;

        const topsFilterItems = props.filterItems.filter(item => item.label.includes('tops'));
        const bottomsFilterItems = props.filterItems.filter(item => item.label.includes('bottoms'));
        const accessoriesFilterItems = props.filterItems.filter(item => item.label.includes('accessories'));
        const footwearFilterItems = props.filterItems.filter(item => item.label.includes('footwear'));

        return html`
          <div class="filter-sizeboxes-container-wrapper">
            ${topsFilterItems.length > 0 ? html` <${args.templates.sizeBoxFilterItemContainer} filterId=${props.filterId} categoryKey="tops" filterTitle=${sizeCategoryCopy.tops} filterItems=${topsFilterItems} /> ` : null}
            ${bottomsFilterItems.length > 0 ? html` <${args.templates.sizeBoxFilterItemContainer} filterId=${props.filterId} categoryKey="bottoms" filterTitle=${sizeCategoryCopy.bottoms} filterItems=${bottomsFilterItems} /> ` : null}
            ${accessoriesFilterItems.length > 0 ? html` <${args.templates.sizeBoxFilterItemContainer} filterId=${props.filterId} categoryKey="accessories" filterTitle=${sizeCategoryCopy.accessories} filterItems=${accessoriesFilterItems} /> ` : null}
            ${footwearFilterItems.length > 0 ? html` <${args.templates.sizeBoxFilterItemContainer} filterId=${props.filterId} categoryKey="footwear" filterTitle=${sizeCategoryCopy.footwear} filterItems=${footwearFilterItems} /> ` : null}
          </div>
        `;
      },
    },

    sizeBoxFilterItemContainer: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const appliedFilters = helpers.getAppliedFilters(true);

        // Use .label (not .id) to derive the category prefix — .label reliably follows the
        // "category_SIZE" convention on both widgets, while .id is an opaque token that
        // Shopify's native filters format differently.
        const firstAppliedFilterCategory = appliedFilters
          .find(filter => TagalysCustomisations.filterHelpers.isCategoryId(filter.id))?.items[0]?.label?.split('_')[0]?.toLowerCase() || '';

        // categoryKey stays in English ('tops', 'bottoms', …) — it has to match the category
        // prefix derived from Tagalys's own filter labels below, and is used as a CSS hook.
        // filterTitle is display-only and translated.
        const { filterItems, filterTitle, filterId, categoryKey } = props;

        const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
        const getSize = (item) => item.label.split('_').pop().toUpperCase();

        const letterItems = filterItems.filter(item => SIZE_ORDER.includes(getSize(item)));
        const numericItems = filterItems.filter(item => !SIZE_ORDER.includes(getSize(item)));

        const sortedFilterItems = [
          ...letterItems.sort((a, b) => SIZE_ORDER.indexOf(getSize(a)) - SIZE_ORDER.indexOf(getSize(b))),
          ...numericItems.sort((a, b) => Number(getSize(a)) - Number(getSize(b))),
        ];

        const hiddenStyle = firstAppliedFilterCategory && firstAppliedFilterCategory !== categoryKey
          ? 'display: none;'
          : '';

        return html`
          <div class="filter-sizeboxes-container filter-sizeboxes-container--${categoryKey}" style=${hiddenStyle}>
            <h3 class="filter-sizeboxes-title">${filterTitle}</h3>
            <ul class="filter-checkboxes filter-sizeboxes">
              ${sortedFilterItems.map(filterItem => html`
                <${args.templates.sizeBoxFilterItem}
                  filterId=${filterId}
                  filterItem=${filterItem}
                >
                  ${filterItem.selected && filterItem.items ? html`
                    <${args.templates.filterSizeBoxes}
                      filterId=${filterId}
                      filterItems=${filterItem.items}
                    />
                  ` : null}
                </${args.templates.sizeBoxFilterItem}>
              `)}
            </ul>
          </div>
        `;
      },
    },

    sizeBoxFilterItem: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;

        const onFilterClick = (e) => {
          e.stopPropagation();
          if (props.filterItem.selected) {
            helpers.clearFilter(props.filterId, props.filterItem.id);
          } else {
            helpers.applyFilter(props.filterId, props.filterItem.id);
          }
          scrollToTop();
        };

        return html`
          <li
            class="filter-item filter-item--size ${props.filterItem.selected ? 'selected' : ''}"
            data-filter-item-id=${props.filterItem.id}
            onClick=${onFilterClick}
          >
            <span class="filter-item-size">${props.filterItem.label.split('_')[1].toUpperCase()}</span>
            ${props.children}
          </li>
        `;
      },
    },

    filterCheckBoxes: {
      render(html, args) {
        const props = args.props;

        return html`
          <ul class="filter-checkboxes">
            ${props.filterItems.map(filterItem => html`
              <${args.templates.checkboxFilterItem}
                filterId=${props.filterId}
                filterItem=${filterItem}
              >
                ${filterItem.selected && filterItem.items ? html`
                  <${args.templates.filterCheckBoxes}
                    filterId=${props.filterId}
                    filterItems=${filterItem.items}
                  />
                ` : null}
              </${args.templates.checkboxFilterItem}>
            `)}
          </ul>
        `;
      },
    },

    checkboxFilterItem: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;

        const onFilterClick = (e) => {
          e.stopPropagation();
          if (props.filterItem.selected) {
            helpers.clearFilter(props.filterId, props.filterItem.id);
          } else {
            helpers.applyFilter(props.filterId, props.filterItem.id);
          }
          scrollToTop();
        };

        return html`
          <li
            class="filter-item"
            data-filter-item-id=${props.filterItem.id}
            onClick=${onFilterClick}
          >
            <span class="filter-checkbox-and-label">
              <input class="checkbox" checked=${props.filterItem.selected} type="checkbox" />
              <span class="filter-item-label-and-count">
                <span class="filter-item-label">${props.filterItem.label}</span>
                ${props.showMatchingProductsCount ? html`
                  <span class="filter-item-count">${props.filterItem.count}</span>
                ` : null}
              </span>
            </span>
            ${props.children}
          </li>
        `;
      },
    },

    appliedFilters: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const appliedFilters = helpers.getAppliedFilters(true)
          .filter(f => !TagalysCustomisations.filterHelpers.REDUNDANT_IDS.includes(f.id));
        const appliedFiltersCount = countAppliedFilters(appliedFilters);

        if (!appliedFiltersCount) return null;

        return html`
          <div class="applied-filters-header">${copy.appliedFilters} (${appliedFiltersCount})</div>
          <ul class="applied-filters">
            ${appliedFilters.map(appliedFilter => {
              if (appliedFilter.type === 'range') {
                return html`
                  <li onClick=${() => { helpers.clearFilter(appliedFilter.id); scrollToTop(); }} class="applied-filter">
                    <span class="applied-filter-label">
                      ${helpers.labelFormatter(appliedFilter.display_format, appliedFilter.selected_min, appliedFilter.currency)}
                      -
                      ${helpers.labelFormatter(appliedFilter.display_format, appliedFilter.selected_max, appliedFilter.currency)}
                    </span>
                    <span class="applied-filter-action-clear">
                      <${args.templates.closeIcon} />
                    </span>
                  </li>
                `;
              }

              return appliedFilter.items.map(filterItem => html`
                <li
                  class="applied-filter"
                  onClick=${() => { helpers.clearFilter(appliedFilter.id, filterItem.id); scrollToTop(); }}
                >
                  <span class="applied-filter-action-clear">
                    <${args.templates.closeIcon} />
                  </span>
                  <span class="applied-filter-label">
                    ${filterItem.label.includes('_') ? html`
                      ${localizedSizeCategory(filterItem.label.split('_')[0])}: ${filterItem.label.split('_')[1]}
                    ` : filterItem.label}
                  </span>
                </li>
              `);
            })}
          </ul>
        `;
      },
    },

    products: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const products = helpers.getProducts();

        const showContentSlots =
          contentSlotsConfig.showContentSlot &&
          contentSlotsConfig.slots.length > 0 &&
          helpers.getCurrentPage() === 1;

        // Ties slot keys to the current product set so a filter/sort change forces Preact to
        // mount fresh slot nodes instead of reusing (and leaving misplaced) ones keyed only by
        // a fixed target position, which stays identical across different filtered results.
        const productSetToken = products.map(product => product.id).join(',');

        const gridItems = [];

        products.forEach((product, index) => {
          const productIndex = index + 1;

          if (showContentSlots) {
            contentSlotsConfig.slots.forEach((slot, slotIndex) => {
              const sizeParts = (slot.size || '1x1').split('x');
              const colSize = sizeParts[0];
              const rowSize = sizeParts[1] || '1';
              const positionDesktop = Number(slot.positionDesktop);
              const positionMobile = Number(slot.positionMobile != null ? slot.positionMobile : slot.positionDesktop);

              if (productIndex === positionDesktop) {
                gridItems.push({
                  type: 'slot',
                  key: `slot-desktop-${slotIndex}-${productIndex}-${productSetToken}`,
                  slot,
                  slotType: 'desktop',
                  colSize,
                  rowSize,
                  productIndex,
                  imageUrl: slot.imageUrl,
                  positionDesktop,
                  positionMobile,
                });
              }

              if (productIndex === positionMobile) {
                gridItems.push({
                  type: 'slot',
                  key: `slot-mobile-${slotIndex}-${productIndex}-${productSetToken}`,
                  slot,
                  slotType: 'mobile',
                  colSize,
                  rowSize,
                  productIndex,
                  imageUrl: slot.mobileImageUrl || slot.imageUrl,
                  positionDesktop: slot.positionDesktop,
                  positionMobile,
                });
              }
            });
          }

          gridItems.push({ type: 'product', key: product.id, product });
        });

        if (window.requestAnimationFrame) {
          window.requestAnimationFrame(() => TagalysCustomisations.productSliders.init());
        } else {
          setTimeout(() => TagalysCustomisations.productSliders.init(), 0);
        }

        return html`
          <div class="ProductListWrapper">
            <div
              id="collection-list"
              class="ProductList ProductList--grid ProductList--removeMargin Grid"
              data-mobile-count=${TagalysCustomisations.layoutSwitcher.state.mobileCount}
              data-desktop-count=${TagalysCustomisations.layoutSwitcher.state.desktopCount}
              data-product-count-per-page="48"
              data-slot-count=${showContentSlots ? contentSlotsConfig.slotCount : undefined}
            >
              ${gridItems.map(item => {
                if (item.type === 'slot') {
                  return html`
                    <${args.templates.contentSlot}
                      key=${item.key}
                      slot=${item.slot}
                      slotType=${item.slotType}
                      colSize=${item.colSize}
                      rowSize=${item.rowSize}
                      productIndex=${item.productIndex}
                      imageUrl=${item.imageUrl}
                      positionDesktop=${item.positionDesktop}
                      positionMobile=${item.positionMobile}
                    />
                  `;
                }

                return html`
                  <${args.templates.product} key=${item.key} product=${item.product} />
                `;
              })}
            </div>
          </div>
        `;
      },
    },

    contentSlot: {
      render(html, args) {
        const props = args.props;
        const slot = props.slot;
        const buttonUrl = typeof slot.buttonUrl === 'string'
          ? slot.buttonUrl
          : slot.buttonUrl && (slot.buttonUrl.url || slot.buttonUrl.value || '');
        const slotTypeClass = props.slotType === 'desktop'
          ? 'c-plp-content-slot-grid-cell--desktop'
          : 'c-plp-content-slot-grid-cell--mobile';
        const textColor = slot.textColor || '#fff';
        const showTitle = slot.showTitle !== false;
        const showDescription = slot.showDescription !== false;
        const isImageClickable = slot.isImageClickable !== false;

        return html`
          <div
            data-product-index=${props.productIndex}
            data-slot-type=${props.slotType}
            data-desktop-position=${props.positionDesktop}
            data-mobile-position=${props.positionMobile}
            data-col-size=${props.colSize}
            data-row-size=${props.rowSize}
            class=${`${TagalysCustomisations.layoutSwitcher.getGridCellClass()} c-plp-content-slot-grid-cell ${slotTypeClass}`}
          >
            <div class="c-plp-content-slot__wrapper">
              <div
                style=${`background-image: url('${props.imageUrl}'); background-size: cover; background-position: center; --text-color: ${textColor};`}
                aria-label=${copy.contentSlotImageAlt}
                class="c-plp-content-slot"
              >
                ${isImageClickable && buttonUrl ? html`
                  <a href=${buttonUrl} class="c-plp-content-slot__abs-link"></a>
                ` : null}
                <div class="c-plp-content-slot__content">
                  ${showTitle && slot.title ? html`
                    <h2 class="c-plp-content-slot__title">${slot.title}</h2>
                  ` : null}
                  ${showDescription && slot.description ? html`
                    <div class="c-plp-content-slot__description">${slot.description}</div>
                  ` : null}
                  ${slot.buttonText && buttonUrl ? html`
                    <a href=${buttonUrl} class="c-plp-content-slot__button">${slot.buttonText}</a>
                  ` : null}
                </div>
              </div>
            </div>
          </div>
        `;
      },
    },

    product: {
      options: {
        productPageUrl: function (product) {
          // Shopify.routes.root is the market-aware storefront root — "/" on the primary
          // market, "/fr-fr/" etc. on a localized subfolder. Deriving the prefix from
          // Shopify.locale instead would give "/fr/", which only matches while the
          // subfolder happens to equal the language code.
          var root =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          return window.location.origin + root + "products/" + product.handle;
        },
      },
      render(html, args) {
        const props = args.props;
        const product = props.product;
        const helpers = props.helpers;

        const productOptions = helpers.getTemplateOptions('product');
        const productLink = productOptions.productPageUrl(product);
        const tags = Array.isArray(product.tags) ? product.tags : [];
        const isUSMarket = TagalysCustomisations.market && TagalysCustomisations.market.countryCode === 'US';
        const isAvailable = !!product.available;
        const hasTag = (tag) => tags.indexOf(tag) !== -1;
        const hasCompareAtPrice = Number(product.compare_at_price) > Number(product.price);
        const isMetafieldSet = (value) => !!value && value !== 'false';
        const hasMultibuyOffer = isMetafieldSet(product.metafields?.custom?.multibuy);
        const hasBackInStockBadge = isMetafieldSet(product.metafields?.custom?.back_in_stock_badge_active);
        const hasBackInStockUSBadge = isMetafieldSet(product.metafields?.custom?.back_in_stock_badge_us);

        let badgeLabelClass = '';
        let badgeText = '';
        let labelListClass = 'ProductItem__LabelList';

        if (((hasTag('restocking_soon_row') && !isUSMarket) || (hasTag('restocking_soon_us') && isUSMarket) || hasTag('restocking_soon')) && !isAvailable) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.restockingSoon;
          labelListClass += ' ProductItem__LabelList-restocking-soon';
        } else if (((hasTag('coming_soon_row') && !isUSMarket) || (hasTag('coming_soon_us') && isUSMarket) || hasTag('coming_soon')) && !isAvailable) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.comingSoon;
          labelListClass += ' ProductItem__LabelList-coming-soon';
        } else if (!isAvailable) {
          badgeLabelClass = 'ProductItem__Label--soldOut Heading Text--subdued badge-colour-dark-orange';
          badgeText = badgeCopy.soldOut;
          labelListClass += ' ProductItem__LabelList-sold-out';
        } else if (hasMultibuyOffer) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.multibuyOffer;
        } else if (hasTag('two-lengths-available')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.twoLengthsAvailable;
        } else if (hasTag('three-lengths-available')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.threeLengthsAvailable;
        } else if (hasTag('Limited Edition')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.limitedEdition;
        } else if (hasTag('Collaboration')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.collaboration;
        } else if (hasTag('New Shape')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.newShape;
        } else if (hasTag('New Silhouette')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.newSilhouette;
        } else if (hasTag('New Boxy Fit')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.newBoxyFit;
        } else if (hasTag('Selvedge')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.selvedge;
        } else if (hasTag('Japanese Selvedge')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.japaneseSelvedge;
        } else if (hasTag('Reversible')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.reversible;
        } else if (hasTag('Italian Fabric')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.italianFabric;
        } else if (hasTag('Made In Italy')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.madeInItaly;
        } else if (hasTag('Italian Leather')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.italianLeather;
        } else if (hasTag('Portuguese Fabric')) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.portugueseFabric;
        } else if (hasBackInStockBadge && !isUSMarket) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.backInStock;
        } else if (hasBackInStockUSBadge && isUSMarket) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading badge-colour-light-grey';
          badgeText = badgeCopy.backInStock;
        } else if (isAvailable && hasCompareAtPrice) {
          badgeLabelClass = 'ProductItem__Label--onSale Heading Text--subdued badge-colour-dark-cyan';
          badgeText = badgeCopy.sale;
        }

        return html`
          <div data-product-id=${product.id} class=${TagalysCustomisations.layoutSwitcher.getGridCellClass()}>
            <div class="ProductItem">
              <div class="ProductItem__Wrapper">
                <a href=${productLink} class="ProductItem__ImageWrapper">
                  <div class="AspectRatio AspectRatio--withFallback" style="max-width: 3700px; padding-bottom: 125.0%; --aspect-ratio: 0.8">
                    <div class="main-carousel">
                      ${product.images.slice(0, 5).map((image, imageIndex) => {
                        if (!image) return html`<div class="carousel-cell"></div>`;

                        const src = image.src || '';
                        const separator = src.indexOf('?') !== -1 ? '&' : '?';
                        const sizedSrc = `${src}${separator}width=600`;

                        if (imageIndex < 2) {
                          return html`
                            <div class="carousel-cell">
                              <img
                                class="ProductItem__Image"
                                src=${sizedSrc}
                                alt=${image.alt}
                                width="600"
                                height="auto"
                              />
                            </div>
                          `;
                        }

                        return html`
                          <div class="carousel-cell">
                            <img
                              class="ProductItem__Image Image--lazyLoad Image--fadeIn"
                              data-src=${`${src}${separator}width={width}`}
                              data-widths="[200,400,600]"
                              data-sizes="auto"
                              alt=${image.alt}
                              width=""
                              height=""
                            /><span class="Image__Loader"></span>
                          </div>
                        `;
                      })}
                    </div>
                  </div>
                  ${badgeText ? html`
                    <div class=${labelListClass}>
                      <span class=${`ProductItem__Label--badge ${badgeLabelClass}`}>${badgeText}</span>
                    </div>
                  ` : null}
                  ${TagalysCustomisations.sizeSwatches.render(html, product)}
                </a>
                <div class="ProductItem__Info ProductItem__Info--left">
                  <h2 class="ProductItem__Title ">
                    <a href=${productLink}>${product.title}</a>
                  </h2>
                  <div class="ProductItem__PriceList  Heading ">
                    <span class="ProductItem__Price Price ${product.compare_at_price && product.price !== product.compare_at_price ? 'Price--highlight' : ''}">${helpers.formatCurrency(product.price)}</span>
                    ${product.compare_at_price && product.price !== product.compare_at_price ? html`<span class="ProductItem__Price Price Price--compareAt Text--subdued">${helpers.formatCurrency(product.compare_at_price)}</span>` : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      },
    },

    productListFooter: {
      render(html, args) {
        return html`
          <div class="products-list-footer">
            <${args.templates.pagination} />
          </div>
        `;
      },
    },

    pagination: {
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;

        if (props.infiniteScroll) {
          if (helpers.canLoadNextResults(props.showLoadNextAfterEvery)) {
            return html`<${args.templates.loadNext} />`;
          }
          return null;
        }

        if (helpers.getTotalPages() <= 1) return null;

        return html`
          <div class="Pagination Text--subdued">
            <div class="Pagination__Nav">
              <${args.templates.paginationLinks} />
            </div>
          </div>
        `;
      },
    },

    paginationLinks: {
      options: {
        showNumberedPageLinks: true,
        numberedPageLinksWindowSize: 3,
        showPreviousPageLink: true,
        showFirstPageLink: false,
        showNextPageLink: true,
        scrollTop() {
          TagalysCustomisations.paginationTransition.onRenderDone();
        },
      },
      render(html, args) {
        const props = args.props;
        const helpers = props.helpers;
        const currentPage = helpers.getCurrentPage();
        const totalPages = helpers.getTotalPages();
        const hasPreviousPage = helpers.hasPreviousPage();
        const hasNextPage = helpers.hasNextPage();
        const isMobile = window.innerWidth < 640;
        const halfWindow = isMobile ? 1 : 2;
        const showAllThreshold = isMobile ? 5 : 6;
        const pagesToDisplay = TagalysCustomisations.paginationHelpers.buildItems(currentPage, totalPages, halfWindow, showAllThreshold);

        return html`
          ${props.showPreviousPageLink && hasPreviousPage ? html`
            <button class="Pagination__NavItem Link Link--primary btn" onclick=${helpers.goToPreviousPage}>
              <svg role="presentation" viewBox="0 0 11 18">
                <path d="M9.5 1.5L1.5 9l8 7.5" stroke-width="2" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path>
              </svg>
            </button>
          ` : null}
          ${props.showNumberedPageLinks ? pagesToDisplay.map(pageDetail => {
            if (pageDetail.type === 'ellipsis') {
              return html`
                <button class="Pagination__NavItem Link Link--primary btn page-jump-over c-pagination-doted-span">
                  ...
                </button>
              `;
            }

            const classes = ['btn'];
            if (pageDetail.current) classes.push('is-active');

            return html`
              <button
                class=${`Pagination__NavItem Link Link--primary ${classes.join(' ')}`}
                onclick=${() => helpers.goToPage(pageDetail.page)}
              >
                ${pageDetail.page}
              </button>
            `;
          }) : null}
          ${props.showNextPageLink && hasNextPage ? html`
            <button class="Pagination__NavItem Link Link--primary btn" onclick=${helpers.goToNextPage}>
              <svg role="presentation" viewBox="0 0 11 18">
                <path d="M1.5 1.5l8 7.5-8 7.5" stroke-width="2" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path>
              </svg>
            </button>
          ` : null}
        `;
      },
    },

    downArrow: {
      render(html) {
        return html`
          <svg class="down-arrow" width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.0006 5.49998L10.4997 1.50048" stroke="#737373" stroke-linecap="square"></path>
            <path d="M6.0006 5.49892L1.50026 1.50055" stroke="#737373" stroke-linecap="square"></path>
          </svg>
        `;
      },
    },

    closeIcon: {
      render(html, args) {
        const props = args.props;
        return html`
          <button onclick=${props.onClick} class="close-icon">
            <svg class="Icon Icon--close" role="presentation" viewBox="0 0 16 14">
              <path d="M15 0L1 14m14 0L1 0" stroke="currentColor" fill="none" fill-rule="evenodd"></path>
            </svg>
          </button>
        `;
      },
    },

    sizeFilters: {
      render(html, args) {
        const props = args.props;
        const { topsSizes, bottomsSizes, accessoriesSizes, footwearSizes } = props;

        return html`
          ${topsSizes.length > 0 ? html` <${args.templates.sizeFilter} sizeFilterId="Tops" sizeFilterSizes=${topsSizes} /> ` : null}
          ${bottomsSizes.length > 0 ? html` <${args.templates.sizeFilter} sizeFilterId="Bottoms" sizeFilterSizes=${bottomsSizes} /> ` : null}
          ${accessoriesSizes.length > 0 ? html` <${args.templates.sizeFilter} sizeFilterId="Accessories" sizeFilterSizes=${accessoriesSizes} /> ` : null}
          ${footwearSizes.length > 0 ? html` <${args.templates.sizeFilter} sizeFilterId="Footwear" sizeFilterSizes=${footwearSizes} /> ` : null}
        `;
      },
    },

    sizeFilter: {
      render(html, args) {
        const props = args.props;
        const { sizeFilterId, sizeFilterSizes } = props;

        return html`
          <!--<div class="size-filter" data-size-filter-id="${sizeFilterId}">
            <p>${sizeFilterId}</p>
            <ul>
              ${sizeFilterSizes.map(size => html`<li>${size}</li>`)}
            </ul>
          </div>-->

          <div data-filter-id="p.m.filter.filter_category" class="filter expanded">
            <${args.templates.filterHeader} filterHeading=${sizeFilterId} />
            <div class="filter-body filter-body-checkboxes">
              <ul class="filter-checkboxes">
                ${sizeFilterSizes.map(size => html`
                  <li data-filter-item-id="${sizeFilterId.toLowerCase()}_${size.toLowerCase()}" class="filter-item">
                    <span class="filter-checkbox-and-label">
                      <input type="checkbox" class="checkbox" />
                      <span class="filter-item-label-and-count">
                        <span class="filter-item-label">${size.toUpperCase()}</span>
                      </span>
                    </span>
                  </li>
                `)}
              </ul>
            </div>
          </div>
        `;
      },
    },
  };

  // Shopify's Storefront API only supports cursor-based pagination, so the
  // ShopifyCollection failover always uses infinite scroll instead of numbered pages.
  const shopifyPagination = {
    render(html, args) {
      const props = args.props;
      const helpers = props.helpers;
      if (helpers.canLoadNextResults(props.showLoadNextAfterEvery)) {
        return html`<${args.templates.loadNext} />`;
      }
      return null;
    },
  };

  const shopifyTemplates = Object.assign({}, commonTemplates, {
    pagination: shopifyPagination,
    // infoAndActions intentionally not overridden — it never calls getTotalProductsCount(),
    // which is the one capability ShopifyCollection lacks.
  });

  const initializeShopifyCollectionFailover = () => {
    return Tagalys.UIWidgets.ShopifyCollection.init('[data-tagalys-collection]', {
      params: { collectionId },
      templates: shopifyTemplates,
    });
  };

  Tagalys.UIWidgets.Collection.init('[data-tagalys-collection]', {
    params: { collectionId },
    failover: initializeShopifyCollectionFailover,
    callbacks: {
      beforeAPICall: function(params, helpers) {
        // Ensure filters exists before reading or adding custom filters.
        params.filters = params.filters || {};
      
        var categoryFilters = params.filters["p.m.filter.filter_category"] || [];
      
        if (categoryFilters.length > 0) {
          // tops_s -> S, bottoms_28 -> 28, tops_xxl -> XXL
          var derivedVariantSizes = categoryFilters.map(function(id) {
            return id.split("_").pop().toUpperCase();
          });
      
          // Deduplicate derived sizes because multiple category filters can resolve to the same size.
          params.filters["_variant_size"] = Array.from(new Set(derivedVariantSizes));
        } else {
          // Clear the derived variant size filter when no category filters are selected.
          delete params.filters["_variant_size"];
        }
      
        // Build a URL-only copy so URL cleanup does not mutate the live API request state.
        var paramsForUrl = Object.assign({}, params);
        paramsForUrl.filters = Object.assign({}, params.filters);
      
        // Omit page=1 only from the browser URL so the default collection page stays clean.
        if (paramsForUrl.page === 1) delete paramsForUrl.page;
      
        var encodedQueryString = helpers.getEncodedQueryString(paramsForUrl);
      
        // Include the derived _variant_size filter in the URL while preserving the clean base URL on page 1.
        window.history.replaceState(window.history.state, "", encodedQueryString ? "?" + encodedQueryString : window.location.pathname);
      
        return params;
      }
    },
    templates: commonTemplates,
  });
};
